import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { PrimeReactProvider } from "primereact/api";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { ProgressSpinner } from "primereact/progressspinner";
import { PRACTICES_SESSION_KEY, CATEGORIES_SESSION_KEY, PRODUCT_FAMILIES_SESSION_KEY, EXCLUDED_OPPORTUNITIES_SESSION_KEY, usePracticeData } from "./hooks/usePracticeData";

import Layout from "./components/Layout/Layout";
import Forbidden from "./pages/Forbidden/Forbidden";
import Dashboard from "./pages/Dashboard/Dashboard";
import ScenarioGenerator from "./pages/ScenarioGenerator/ScenarioGenerator";
import ScenarioResults from "./pages/ScenarioResults/ScenarioResults";
import FavoriteScenarios from "./pages/FavoriteScenarios/FavoriteScenarios";
import AccountSettings from "./pages/AccountSettings/AccountSettings";

import { ScenarioProvider, scenarioActions, useScenarioContext } from "./context/ScenarioContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useForbidden, setForbidden } from "./services/permissionState";

// Full-screen centered spinner used while we verify access.
const FullScreenSpinner: React.FC<{ label?: string }> = ({ label }) => (
  <div className="flex justify-content-center align-items-center" style={{ height: '100vh' }}>
    <div className="text-center">
      <style>{`
        .blue-spinner .p-progress-spinner-circle {
          stroke: #173A63 !important;
        }
      `}</style>
      <ProgressSpinner
        className="blue-spinner"
        style={{ width: '80px', height: '80px' }}
        strokeWidth="4"
      />
      {label && <h2 className="mt-4" style={{ color: '#6c757d' }}>{label}</h2>}
    </div>
  </div>
);

// Gates the entire authenticated app behind the practices/role check.
// This component only mounts inside <AuthenticatedTemplate>, so the practices
// call only runs once the user is signed in.
//   - while the (uncached) practices call is in flight  -> spinner
//   - if it returns 403 (no required role)              -> Forbidden page
//   - otherwise                                         -> the app
const AuthenticatedApp: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
  const isForbidden = useForbidden();
  const { isLoading: practicesLoading } = usePracticeData();

  if (isForbidden) {
    return <Forbidden onLogout={onLogout} />;
  }

  if (practicesLoading) {
    return <FullScreenSpinner label="Loading..." />;
  }

  return (
    <Layout onLogout={onLogout}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/generate" element={<ScenarioGenerator />} />
        <Route path="/results" element={<ScenarioResults />} />
        <Route path="/favorites" element={<FavoriteScenarios />} />
        <Route path="/account-settings" element={<AccountSettings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

const AppRoutes: React.FC = () => {
  const { dispatch } = useScenarioContext();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    setForbidden(false);
    dispatch(scenarioActions.clearAll());
    localStorage.removeItem("scenarioState");
    sessionStorage.removeItem(PRACTICES_SESSION_KEY);
    sessionStorage.removeItem(CATEGORIES_SESSION_KEY);
    sessionStorage.removeItem(PRODUCT_FAMILIES_SESSION_KEY);
    sessionStorage.removeItem(EXCLUDED_OPPORTUNITIES_SESSION_KEY);
  };

  return (
    <>
      <AuthenticatedTemplate>
        <AuthenticatedApp onLogout={handleLogout} />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <FullScreenSpinner label="Redirecting..." />
      </UnauthenticatedTemplate>
    </>
  );
};

const App: React.FC = () => (
  <PrimeReactProvider>
    <ScenarioProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ScenarioProvider>
  </PrimeReactProvider>
);

export default App;
